import type { ReactNode } from "react";
import type { ConversationView, DmMessageView } from "@townsy/contracts";
import { formatUtcDateTime } from "../../lib/format";
import { FlagIcon } from "../FlagIcon";
import { ReportFlagIcon } from "../people/ReportFlagIcon";

/**
 * Presentational pieces of the private messages dialog (ADR-0014; founder feedback 2026-10-14: "messages ... look too
 * plain"): initials avatars, the conversation list rows, chat bubbles with day separators, and the empty state. Message
 * text is always plain text (React escapes it; links are never made clickable).
 */

/** Up to two initials: two words -> their first letters; one CamelCase word -> its first two capitals; else one letter. */
export function initialsOf(label: string): string {
  const words = label
    .trim()
    .split(/[\s_\-.]+/)
    .filter(Boolean);
  const letter = (w: string) => Array.from(w)[0] ?? "";
  if (words.length >= 2) return (letter(words[0]!) + letter(words[1]!)).toUpperCase();
  const word = words[0] ?? "";
  const caps = word.match(/\p{Lu}/gu) ?? [];
  if (caps.length >= 2 && word.startsWith(caps[0]!)) return caps[0]! + caps[1]!;
  return letter(word).toUpperCase() || "?";
}

/** Avatar backgrounds: each passes 4.5:1 with white initials. Picked by name, so a person keeps their colour. */
export const AVATAR_COLORS = ["#2851d9", "#6a45c8", "#b4387a", "#0b7285", "#b4501f", "#4054b2", "#8e3b8e", "#1d6fa5"] as const;

export function avatarColor(label: string): string {
  let h = 2166136261;
  for (const ch of label.toLowerCase()) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

export function Avatar({ label, size = 40 }: { label: string; size?: number }) {
  return (
    <span className="msgr-avatar" style={{ width: size, height: size, background: avatarColor(label), fontSize: size * 0.38 }} aria-hidden="true">
      {initialsOf(label)}
    </span>
  );
}

const pad = (n: number) => String(n).padStart(2, "0");
/** Local calendar day key (the viewer's own days for separators and list times). */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "Today", "Yesterday", "Mon, Oct 12" or "Oct 12, 2025" (another year). */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const key = localDayKey(iso);
  if (key === localDayKey(now.toISOString())) return "Today";
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (key === localDayKey(y.toISOString())) return "Yesterday";
  return d.toLocaleDateString("en-US", d.getFullYear() === now.getFullYear() ? { weekday: "short", month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

export function timeLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** The list's time column: a time today, a weekday this week, otherwise a date. */
export function listTimeLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (localDayKey(iso) === localDayKey(now.toISOString())) return timeLabel(iso);
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  if (days < 6) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", d.getFullYear() === now.getFullYear() ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "2-digit" });
}

export type ThreadItem =
  | { kind: "day"; key: string; label: string }
  | { kind: "message"; key: string; message: DmMessageView; /** First of a run from the same sender. */ first: boolean; /** Last of a run: shows the time. */ last: boolean };

const RUN_GAP_MS = 5 * 60_000;

/** Messages (oldest first) with a separator before each new local day, grouped into runs (same sender, < 5 min apart). */
export function threadItems(messages: DmMessageView[], now = new Date()): ThreadItem[] {
  const out: ThreadItem[] = [];
  let lastDay = "";
  messages.forEach((m, i) => {
    const day = localDayKey(m.at);
    if (day !== lastDay) {
      out.push({ kind: "day", key: `day-${day}`, label: dayLabel(m.at, now) });
      lastDay = day;
    }
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const joins = (a: DmMessageView | undefined, b: DmMessageView | undefined) =>
      Boolean(a && b && a.fromMe === b.fromMe && localDayKey(a.at) === localDayKey(b.at) && Math.abs(new Date(b.at).getTime() - new Date(a.at).getTime()) < RUN_GAP_MS);
    out.push({ kind: "message", key: m.messageId, message: m, first: !joins(prev, m), last: !joins(m, next) });
  });
  return out;
}

/** One line of the list preview (yours marked, whitespace folded, long text shortened). */
export function previewOf(c: Pick<ConversationView, "lastMessage">): string {
  if (!c.lastMessage) return "No messages yet";
  const t = c.lastMessage.text.replace(/\s+/g, " ");
  return `${c.lastMessage.fromMe ? "You: " : ""}${t.length > 60 ? `${t.slice(0, 59)}…` : t}`;
}

export function ConversationRow({ conversation: c, active, onSelect, now }: { conversation: ConversationView; active: boolean; onSelect(): void; now?: Date }) {
  const at = c.lastMessage?.at ?? c.updatedAt;
  const unread = c.unreadCount > 0 && !c.mutedByMe;
  return (
    <button
      type="button"
      className={`msgr-item${active ? " active" : ""}${unread ? " unread" : ""}${c.mutedByMe ? " muted" : ""}`}
      aria-current={active ? "true" : undefined}
      onClick={onSelect}
      data-testid="dm-list-item"
      data-conversation-id={c.conversationId}
    >
      <Avatar label={c.other.label} size={42} />
      <span className="msgr-item-main">
        <span className="msgr-item-top">
          <span className="msgr-item-name">
            <FlagIcon code={c.other.countryCode} height={13} /> {c.other.label}
          </span>
          <time className="msgr-item-time" dateTime={at} title={formatUtcDateTime(at)}>
            {listTimeLabel(at, now)}
          </time>
        </span>
        <span className="msgr-item-bottom">
          <span className="msgr-item-preview">{c.mutedByMe ? "Muted · messages hidden" : previewOf(c)}</span>
          {unread ? (
            <span className="msgr-unread" aria-label={`${c.unreadCount} unread`}>
              {c.unreadCount > 99 ? "99+" : c.unreadCount}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}

/** The bubbles: yours on the right, theirs on the left; their messages can be reported one by one. */
export function MessageBubbles({
  messages,
  otherLabel,
  onReport,
  now,
}: {
  messages: DmMessageView[];
  otherLabel: string;
  onReport?: (m: DmMessageView) => void;
  now?: Date;
}) {
  return (
    <>
      {threadItems(messages, now).map((item) =>
        item.kind === "day" ? (
          <li key={item.key} className="msgr-day" role="presentation">
            <span>{item.label}</span>
          </li>
        ) : (
          <li
            key={item.key}
            className={`dm-message msgr-msg${item.message.fromMe ? " mine" : " theirs"}${item.first ? " first" : ""}${item.last ? " last" : ""}`}
            data-testid="dm-message"
            data-from-me={item.message.fromMe ? "true" : "false"}
            data-message-id={item.message.messageId}
          >
            <span className="visually-hidden">{item.message.fromMe ? "You" : otherLabel}: </span>
            <span className="msgr-bubble-row">
              <span className="msgr-bubble pre-wrap" title={formatUtcDateTime(item.message.at)}>
                {item.message.text}
              </span>
              {!item.message.fromMe && onReport ? (
                <button
                  type="button"
                  className="msgr-report-btn"
                  aria-label={`Report this message from ${otherLabel}`}
                  title="Report this message"
                  onClick={() => onReport(item.message)}
                  data-testid="dm-message-report"
                >
                  <ReportFlagIcon />
                </button>
              ) : null}
            </span>
            {item.last ? (
              <time className="msgr-time" dateTime={item.message.at}>
                {timeLabel(item.message.at)}
              </time>
            ) : null}
          </li>
        ),
      )}
    </>
  );
}

/** Townsy's own illustration for the empty messages screen: two speech bubbles over a little street. */
export function MessagesIllustration({ size = 168 }: { size?: number }) {
  return (
    <svg className="msgr-illustration" width={size} height={size * 0.72} viewBox="0 0 200 144" aria-hidden="true" focusable="false">
      <ellipse cx="100" cy="128" rx="82" ry="10" fill="#e9e2d2" />
      <rect x="30" y="70" width="34" height="56" rx="4" fill="#dfe7fb" />
      <rect x="36" y="78" width="9" height="9" rx="1.5" fill="#fff" />
      <rect x="49" y="78" width="9" height="9" rx="1.5" fill="#fff" />
      <rect x="42" y="106" width="10" height="20" rx="1.5" fill="#2851d9" opacity="0.55" />
      <path d="M26 72 47 56l21 16z" fill="#c9d6f7" />
      <rect x="140" y="64" width="36" height="62" rx="4" fill="#fbe8f0" />
      <rect x="146" y="72" width="10" height="10" rx="1.5" fill="#fff" />
      <rect x="160" y="72" width="10" height="10" rx="1.5" fill="#fff" />
      <rect x="146" y="88" width="10" height="10" rx="1.5" fill="#fff" />
      <rect x="160" y="88" width="10" height="10" rx="1.5" fill="#fff" />
      <rect x="153" y="106" width="10" height="20" rx="1.5" fill="#b4387a" opacity="0.5" />
      <circle cx="84" cy="104" r="7" fill="#6a45c8" />
      <rect x="77" y="111" width="14" height="17" rx="6" fill="#6a45c8" />
      <circle cx="116" cy="104" r="7" fill="#d99a2b" />
      <rect x="109" y="111" width="14" height="17" rx="6" fill="#d99a2b" />
      <rect x="56" y="18" width="62" height="34" rx="12" fill="#2851d9" />
      <path d="M74 52 70 64l14-12z" fill="#2851d9" />
      <rect x="66" y="29" width="34" height="4" rx="2" fill="#fff" opacity="0.9" />
      <rect x="66" y="37" width="22" height="4" rx="2" fill="#fff" opacity="0.7" />
      <rect x="98" y="40" width="56" height="30" rx="12" fill="#fff" stroke="#c9c0ab" strokeWidth="1.5" />
      <path d="M128 70 136 80l2-10z" fill="#fff" stroke="#c9c0ab" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M129 69.2h8" stroke="#fff" strokeWidth="3" />
      <circle cx="114" cy="55" r="3" fill="#d99a2b" />
      <circle cx="126" cy="55" r="3" fill="#d99a2b" />
      <circle cx="138" cy="55" r="3" fill="#d99a2b" />
    </svg>
  );
}

export function MessagesEmptyState({ onShowHow, children }: { onShowHow(): void; children?: ReactNode }) {
  return (
    <div className="msgr-empty" data-testid="dm-empty">
      <MessagesIllustration size={220} />
      <h3 className="msgr-empty-title">No conversations yet</h3>
      <p className="msgr-empty-text">Click a person in town and choose Message. Private messages are between verified accounts only.</p>
      <button type="button" className="btn btn-primary" onClick={onShowHow} data-testid="dm-empty-how">
        Show me how
      </button>
      {children}
    </div>
  );
}
